
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { CheckCircle, XCircle, Clock, User } from "lucide-react";
import { useTimelineApprovals, PendingApproval } from "@/hooks/useTimelineApprovals";
import { useTranslation } from "react-i18next";

const TimelineApprovals = () => {
  const { pendingApprovals, isLoading, approveEntry, rejectEntry, isApproving, isRejecting } = useTimelineApprovals();
  const [selectedEntry, setSelectedEntry] = useState<PendingApproval | null>(null);
  const [approvalNotes, setApprovalNotes] = useState("");
  const [rejectionNotes, setRejectionNotes] = useState("");
  const { t } = useTranslation('timeline');

  if (isLoading) {
    return (
      <Card className="uplivion-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Pending Approvals
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-gray-500">Loading...</div>
        </CardContent>
      </Card>
    );
  }

  if (pendingApprovals.length === 0) {
    return (
      <Card className="uplivion-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Pending Approvals
          </CardTitle>
          <CardDescription>
            No entries waiting for your approval
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const handleApprove = (entryId: string, notes?: string) => {
    approveEntry({ entryId, notes });
    setSelectedEntry(null);
    setApprovalNotes("");
  };

  const handleReject = (entryId: string, notes?: string) => {
    rejectEntry({ entryId, notes });
    setSelectedEntry(null);
    setRejectionNotes("");
  };

  const getPrivacyLevelBadge = (level: string) => {
    const variants = {
      'creator_and_owner': 'secondary',
      'mutual_friends': 'outline',
      'timeline_access': 'default',
      'friends': 'default',
      'public': 'destructive'
    } as const;

    const labels = {
      'creator_and_owner': 'Private',
      'mutual_friends': 'Mutual Friends',
      'timeline_access': 'Timeline Access',
      'friends': 'Friends',
      'public': 'Public'
    };

    return (
      <Badge variant={variants[level as keyof typeof variants] || 'default'}>
        {labels[level as keyof typeof labels] || level}
      </Badge>
    );
  };

  return (
    <Card className="uplivion-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Pending Approvals ({pendingApprovals.length})
        </CardTitle>
        <CardDescription>
          Friends have requested to add memories to your timeline
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {pendingApprovals.map((entry) => (
            <div key={entry.id} className="uplivion-card p-4 space-y-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-semibold">{entry.title}</h4>
                  {entry.content && (
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                      {entry.content}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-2">
                    <User className="h-4 w-4 text-gray-500" />
                    <span className="text-sm text-gray-600">
                      {entry.creator_profile?.first_name} {entry.creator_profile?.last_name} 
                      (@{entry.creator_profile?.username})
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    {getPrivacyLevelBadge(entry.privacy_level)}
                    <span className="text-xs text-gray-500">
                      Requested {new Date(entry.approval_requested_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-2">
                <Dialog>
                  <DialogTrigger asChild>
                    <Button 
                      size="sm" 
                      className="gap-1"
                      onClick={() => setSelectedEntry(entry)}
                    >
                      <CheckCircle className="h-4 w-4" />
                      Approve
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Approve Memory</DialogTitle>
                      <DialogDescription>
                        This will add "{entry.title}" to your timeline and make it visible according to the privacy settings.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <Textarea
                        placeholder="Add an optional note (visible to the creator)"
                        value={approvalNotes}
                        onChange={(e) => setApprovalNotes(e.target.value)}
                      />
                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setSelectedEntry(null);
                            setApprovalNotes("");
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={() => handleApprove(entry.id, approvalNotes)}
                          disabled={isApproving}
                        >
                          {isApproving ? 'Approving...' : 'Approve'}
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>

                <Dialog>
                  <DialogTrigger asChild>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="gap-1"
                      onClick={() => setSelectedEntry(entry)}
                    >
                      <XCircle className="h-4 w-4" />
                      Reject
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Reject Memory</DialogTitle>
                      <DialogDescription>
                        This will permanently remove "{entry.title}" from your timeline approval queue.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <Textarea
                        placeholder="Add an optional note explaining why (visible to the creator)"
                        value={rejectionNotes}
                        onChange={(e) => setRejectionNotes(e.target.value)}
                      />
                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setSelectedEntry(null);
                            setRejectionNotes("");
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="destructive"
                          onClick={() => handleReject(entry.id, rejectionNotes)}
                          disabled={isRejecting}
                        >
                          {isRejecting ? 'Rejecting...' : 'Reject'}
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default TimelineApprovals;
